import { crmRegistry } from "@/features/crm_system/lib/_core/crmRegistry";
import { AllCrmsFieldsTypes } from "@/features/crm_system/lib/_core/crmSystem";
import { Tables } from "@/lib/supabase/supabase_types_override";
import { supabase } from "@/lib/supabase/supabaseClient";
import { merge } from "lodash";
import { CopySurveyToProjectParams } from ".";
import { prepareQuestionsWithNewIds } from "@/lib/utils/replaceOldIdsWithNewIds";
import { DEFAULT_CRM_SYSTEM } from "@/features/crm_system/lib/_core/crmSystem/crmSystem.constants.gen";

const querySurveyBankData = async (surveyId: Tables<"survey_bank">["id"]) =>
  await supabase
    .from("survey_bank")
    .select(`*, question:survey_bank_question_bank(*), crm(*)`)
    .eq("id", surveyId)
    .single();

const querySurveyData = async (surveyId: Tables<"survey">["id"]) =>
  await supabase
    .from("survey")
    .select(`*, question(*), crm(*)`)
    .eq("id", surveyId)
    .single();

export const copySurveyToProject = async ({
  id: surveyId,
  copyFromBank = false,
  ...updatedBody
}: CopySurveyToProjectParams) => {
  try {
    const { data: toProjectData, error: toProjectError } = await supabase
      .from("project")
      .select("*")
      .eq("id", updatedBody.project_id)
      .single();
    if (toProjectError) {
      console.error(toProjectError);
      throw new Error(
        `Failed to fetch project with id ${updatedBody.project_id}`
      );
    }
    if (!toProjectData) {
      console.error("No project data returned");
      throw new Error(
        `Failed to fetch project with id ${updatedBody.project_id}`
      );
    }
    let copiedSurveyQueryRes: Awaited<
      ReturnType<typeof querySurveyBankData | typeof querySurveyData>
    >;
    if (!surveyId) throw new Error("Survey ID is required");
    if (copyFromBank) {
      copiedSurveyQueryRes = await querySurveyBankData(surveyId);
    } else {
      copiedSurveyQueryRes = await querySurveyData(surveyId);
    }
    const { data: copiedSurveyData, error: copiedSurveyError } =
      copiedSurveyQueryRes;

    if (copiedSurveyError) {
      console.error(copiedSurveyError);
      throw new Error(`Failed to fetch survey with id ${surveyId}`);
    }

    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    let {
      question: questions,
      id: copiedSurveyId,
      crm,
      created_at,
      crm_system,
      survey_ref_id,
      ...copiedSurveyBody
    } = copiedSurveyData;

    const baseBody = { ...updatedBody, archived: false };
    const surveyBankData =
      copyFromBank && isSurveyFromSurveyBank(copiedSurveyData)
        ? { survey_bank_ref_id: copiedSurveyId }
        : {};
    const newSurveyBody = merge(copiedSurveyBody, baseBody, surveyBankData);
    const { data: newSurveyData, error: newSurveyError } = await supabase
      .from("survey")
      .insert(newSurveyBody)
      .select()
      .single();
    if (newSurveyError) {
      console.log(newSurveyBody);
      console.error(newSurveyError);
      throw new Error(`Failed to create new survey`);
    }
    if (!newSurveyData) {
      console.error("No new survey data returned");
      throw new Error("Failed to create new survey");
    }
    const createdQuestions: Tables<"question">[] = [];
    questions = questions.map((q) => ({ ...q, survey_bank_question_bank_ref_id: q.id }));
    const questionsWithNewIds = prepareQuestionsWithNewIds({ questions });

    for (const question of questionsWithNewIds) {
      // eslint-disable-next-line @typescript-eslint/no-unused-vars
      const {
        created_at,
        crm_id,
        organisation_id,
        survey_bank_id,
        survey_bank_question_bank_ref_id,
        ...questionBody
      } = question;
      const newCrmSystem = toProjectData.crm_system || DEFAULT_CRM_SYSTEM;
      let newField: AllCrmsFieldsTypes | undefined;
      const field_type_context_stack = question.field_type_context_stack;
      // TODO Change schema to use field_type: z.enum(fieldType)
      if (copiedSurveyData?.crm?.crm_system === newCrmSystem) {
        newField = question.field_type as AllCrmsFieldsTypes | undefined;
        if (!newField) {
          console.error(
            `Field type is not defined for question with id ${question.id} in survey with id ${surveyId}`
          );
          throw new Error(
            `Field type is not defined for question with id ${question.id}`
          );
        }
      } else {
        newField = field_type_context_stack.find(
          (field) => field.crm_system === newCrmSystem
        )?.field_type as AllCrmsFieldsTypes | undefined;
        if (!newField) {
          newField = crmRegistry.parseFieldTypeToOtherCrmSystem(
            copiedSurveyData?.crm?.crm_system || DEFAULT_CRM_SYSTEM,
            newCrmSystem,
            question.field_type
          );
          if (newField) {
            field_type_context_stack.push({
              crm_system: newCrmSystem,
              field_type: newField,
            });
          }
        }

        if (!newField) {
          console.log(question);
          console.error(
            `Failed to convert field type from ${
              copiedSurveyData?.crm?.crm_system || DEFAULT_CRM_SYSTEM
            } to ${newCrmSystem} for question with id ${question.id}`
          );
          throw new Error(
            `Failed to convert field type for question with id ${question.id}`
          );
        }
      }
      const ifFromSurveyBank =
        copyFromBank && isSurveyFromSurveyBank(copiedSurveyData)
          ? { survey_bank_question_bank_ref_id: question.survey_bank_question_bank_ref_id }
          : {};
      // crm_system: newCrmSystem, field_type: newField, survey_id: newSurvey
      const newQuestionBody = merge(
        questionBody,
        {
          survey_id: newSurveyData.id,
          field_type: newField,
          crm_system: newCrmSystem,
          archived: false,
          field_type_context_stack,
        },
        ifFromSurveyBank
      );
      console.log(newQuestionBody);
      const { data: newQuestionData, error: newQuestionError } = await supabase
        .from("question")
        .insert(newQuestionBody)
        .select()
        .single();
      if (newQuestionError) {
        await supabase.from("survey").delete().eq("id", newSurveyData.id);
        for (const createdQuestion of createdQuestions) {
          await supabase.from("question").delete().eq("id", createdQuestion.id);
        }
        console.error(newQuestionError);
        throw new Error(`Failed to copy question with id ${question.id}`);
      }
      createdQuestions.push(newQuestionData);
    }
    return { data: merge(newSurveyData, { question: createdQuestions }) };
  } catch (error) {
    console.error(error);
    throw error;
  }
};

function isSurveyFromSurveyBank(
  survey: Tables<"survey_bank">
): survey is Tables<"survey_bank"> {
  return "survey_bank_ref_id" in survey || true;
}
